import { useEffect, useRef, useCallback } from "react";
import echo from "../lib/echo";

/**
 * Custom hook to manage real-time comment updates for questions via WebSocket
 *
 * @param {Array} questions - Array of questions with their comments
 * @param {Function} setQuestions - State setter for questions
 * @param {string} projectId - Current project ID
 */
export function useQuestionComments(questions, setQuestions, projectId) {
  const pendingActionsRef = useRef(new Set());
  const subscribedChannelsRef = useRef(new Set());

  // Track actions initiated by this user
  const addPendingAction = useCallback((type, id) => {
    const key = `${type}-${id}`;
    pendingActionsRef.current.add(key);

    // Note: Removed auto-timeout to ensure pending actions persist until WebSocket events are processed
    // The WebSocket handler will remove the pending action when it skips the event
  }, []);

  const isPendingAction = useCallback((type, id) => {
    const key = `${type}-${id}`;
    return pendingActionsRef.current.has(key);
  }, []);

  // Subscribe to WebSocket events for all questions
  useEffect(() => {
    if (!questions || questions.length === 0) return;

    // Subscribe to channels for each question
    questions.forEach((question) => {
      const channelName = `question.${question.id}`;

      // Skip if already subscribed
      if (subscribedChannelsRef.current.has(channelName)) {
        return;
      }

      const channel = echo
        .channel(channelName)
        .listen(".comment.added", (event) => {
          // Validate event structure
          if (!event || !event.comment) {
            return;
          }

          const commentId = event.comment.id;
          const questionId = event.comment.question_id;

          // Skip if this user just added it
          if (isPendingAction("add", commentId)) {
            pendingActionsRef.current.delete(`add-${commentId}`);
            return;
          }

          // Add comment for other users with animation flag
          setQuestions((prev) =>
            prev.map((q) => {
              // Validate comment data
              if (!event.comment || event.comment.id === undefined || event.comment.id === null) {
                return q;
              }

              if (q.id === questionId) {
                // Prevent duplicates - validate both IDs are valid numbers
                const incomingCommentId = Number(event.comment.id);
                if (isNaN(incomingCommentId)) {
                  return q;
                }

                if (q.comments?.some((c) => Number(c.id) === incomingCommentId)) {
                  return q;
                }

                // Add animation flag for new comments from other users
                const commentWithAnimation = {
                  ...event.comment,
                  _isNew: true,
                };

                return {
                  ...q,
                  comments: [...(q.comments || []), commentWithAnimation],
                  status: q.status === "untouched" ? "in_progress" : q.status,
                };
              }
              return q;
            }),
          );

          // Remove animation flag after animation completes
          setTimeout(() => {
            setQuestions((prev) =>
              prev.map((q) => {
                if (q.id === event.comment.question_id) {
                  return {
                    ...q,
                    comments: q.comments?.map((c) =>
                      c.id === event.comment.id ? { ...c, _isNew: false } : c,
                    ),
                  };
                }
                return q;
              }),
            );
          }, 300);

          // Forward notification payload to notifications provider
          if (event.notification) {
            try {
              window.dispatchEvent(
                new CustomEvent("app:new-notification", { detail: event.notification }),
              );
            } catch (e) {}
          }
        })
        .listen(".comment.deleted", (event) => {
          // Skip if this user just deleted it
          if (isPendingAction("delete", event.comment_id)) {
            pendingActionsRef.current.delete(`delete-${event.comment_id}`);
            return;
          }

          // Mark comment for deletion with animation flag
          setQuestions((prev) =>
            prev.map((q) => {
              if (q.id === event.question_id) {
                return {
                  ...q,
                  comments: q.comments?.map((c) =>
                    c.id === event.comment_id ? { ...c, _isDeleting: true } : c,
                  ),
                };
              }
              return q;
            }),
          );

          // Remove comment after animation completes
          setTimeout(() => {
            setQuestions((prev) =>
              prev.map((q) => {
                if (q.id === event.question_id) {
                  return {
                    ...q,
                    comments: q.comments?.filter(
                      (c) => c.id !== event.comment_id,
                    ),
                  };
                }
                return q;
              }),
            );
          }, 200);
        });

      subscribedChannelsRef.current.add(channelName);
    });

    // Cleanup: unsubscribe from channels for questions that no longer exist
    return () => {
      const currentQuestionIds = new Set(
        questions.map((q) => `question.${q.id}`),
      );
      subscribedChannelsRef.current.forEach((channelName) => {
        if (!currentQuestionIds.has(channelName)) {
          echo.leave(channelName);
          subscribedChannelsRef.current.delete(channelName);
        }
      });
    };
  }, [questions, isPendingAction]);

  // Return methods to track user actions
  return {
    addPendingAction,
    isPendingAction,
  };
}

export default useQuestionComments;
